import { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { fetchSupportAttachmentLimits, uploadSupportImage, type SupportAttachmentLimits } from '../../services/api';
import { apiError } from './contactAdmin';

const ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];
/** Used until the server's limits arrive (the backend checks again). */
const FALLBACK_LIMITS: SupportAttachmentLimits = { maxBytes: 5 * 1024 * 1024, maxMegabytes: 5, maxPerMessage: 2, maxPerTicketPerDay: 10 };

type Picked = { key: string; id: string | null; preview: string; name: string };

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Images on a Contact admin message: the Shop picks JPG, PNG or WEBP files, each is uploaded at
 * once, and the ids of the uploaded ones go out with the message (onChange). Remounting it (a new
 * `key`) clears it after sending.
 */
export function ContactAdminImagePicker({
  onChange,
  disabled = false,
}: {
  onChange: (ids: string[], uploading: boolean) => void;
  disabled?: boolean;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [limits, setLimits] = useState<SupportAttachmentLimits>(FALLBACK_LIMITS);
  const [picked, setPicked] = useState<Picked[]>([]);
  const uploading = picked.some((p) => !p.id);

  useEffect(() => {
    let alive = true;
    fetchSupportAttachmentLimits()
      .then((res) => alive && res.success && res.data && setLimits(res.data))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    onChange(picked.filter((p) => p.id).map((p) => p.id as string), uploading);
  }, [picked, uploading, onChange]);

  useEffect(() => () => picked.forEach((p) => URL.revokeObjectURL(p.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = limits.maxPerMessage - picked.length;
    const list = Array.from(files);
    if (list.length > room) toast.error(`You can add up to ${limits.maxPerMessage} images to one message.`);
    for (const file of list.slice(0, Math.max(0, room))) {
      if (!ACCEPT.includes(file.type) || file.size >= limits.maxBytes) {
        toast.error(`Use a JPG, PNG or WEBP image under ${limits.maxMegabytes} MB.`);
        continue;
      }
      const key = `${file.name}-${file.size}-${Math.random()}`;
      const preview = URL.createObjectURL(file);
      setPicked((prev) => [...prev, { key, id: null, preview, name: file.name }]);
      try {
        const res = await uploadSupportImage(await readAsDataUrl(file));
        if (!res.success || !res.data) throw new Error('upload failed');
        const id = res.data.id;
        setPicked((prev) => prev.map((p) => (p.key === key ? { ...p, id } : p)));
      } catch (error) {
        toast.error(apiError(error, 'Could not add that image').message);
        URL.revokeObjectURL(preview);
        setPicked((prev) => prev.filter((p) => p.key !== key));
      }
    }
  };

  const remove = (key: string) =>
    setPicked((prev) => {
      const gone = prev.find((p) => p.key === key);
      if (gone) URL.revokeObjectURL(gone.preview);
      return prev.filter((p) => p.key !== key);
    });

  return (
    <div className="flex flex-wrap items-center gap-2">
      {picked.map((p) => (
        <span key={p.key} className="relative inline-flex">
          <img src={p.preview} alt={p.name} className="h-14 w-14 rounded-lg border border-stone-200 object-cover" />
          {!p.id ? (
            <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70">
              <Loader2 className="h-4 w-4 animate-spin text-stone-600" aria-label="Uploading" />
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => remove(p.key)}
            className="absolute -right-1.5 -top-1.5 rounded-full bg-stone-800 p-0.5 text-white hover:bg-stone-900"
            aria-label={`Remove ${p.name}`}
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </span>
      ))}
      {picked.length < limits.maxPerMessage ? (
        <>
          <label
            htmlFor={inputId}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-stone-300 px-3 py-2 text-sm text-stone-700 hover:border-emerald-400 hover:text-emerald-800 ${
              disabled ? 'pointer-events-none opacity-50' : ''
            }`}
          >
            <ImagePlus className="h-4 w-4" aria-hidden /> Add image
          </label>
          <input
            id={inputId}
            ref={inputRef}
            type="file"
            accept={ACCEPT.join(',')}
            multiple
            disabled={disabled}
            className="sr-only"
            onChange={(e) => {
              void add(e.target.files);
              if (inputRef.current) inputRef.current.value = '';
            }}
          />
        </>
      ) : null}
      <span className="text-xs text-stone-500">
        JPG, PNG or WEBP under {limits.maxMegabytes} MB · up to {limits.maxPerMessage} per message
      </span>
    </div>
  );
}

/** Images on a message in the conversation; each opens full size in a new tab. */
export function ContactAdminImageList({ images }: { images: Array<{ id: string; url: string }> }) {
  if (!images.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {images.map((img) => (
        <a key={img.id} href={img.url} target="_blank" rel="noopener noreferrer" className="block">
          <img src={img.url} alt="Attached image" className="h-24 w-24 rounded-lg border border-stone-200 object-cover" />
        </a>
      ))}
    </div>
  );
}
